/**
 * ViewGridNew - MUI X DataGrid implementation
 *
 * Replaces the basic MUI Joy Table with MUI X DataGrid for:
 * - Cell-level editing
 * - Server-side pagination
 * - Column sorting
 * - Better performance with large datasets
 *
 * Based on original MyVault ViewGrid.tsx patterns.
 */

import React, { useState, useCallback, useMemo } from "react";
import { Box, Alert, Typography, Snackbar, Tooltip } from "@mui/joy";
import { ThemeProvider } from "@mui/material/styles";
import {
  materialGridThemeTokens,
  useMaterialGridTheme,
} from "./materialGridTheme";
import {
  DataGrid,
  GridToolbar,
  GridCellParams,
  GridRowId,
  GridCellModesModel,
  GridCellModes,
  GridPaginationModel,
  GridSortModel,
  GridRowModel,
  GridRowParams,
  GridRowSelectionModel,
  GridFilterModel,
  GridLogicOperator,
  useGridApiRef,
} from "@mui/x-data-grid";
import type { ViewDefinition } from "../../types";
import { getViewRowId } from "../../services/viewService";
import type { ProcessedViewData } from "../../services/viewService";
import type { CanvasWriteContext } from "../../services/viewService";
import { orderedVisibleColumns } from "./orderedVisibleColumns";
import { useViewGridColumns } from "./useViewGridColumns";
import { applyColumnDisplay, type ColumnDisplayMap } from "./columnDisplay";
import {
  serverFilterableColumns,
  serverFilterOperators,
  toServerFilters,
  type ColumnFilter,
} from "./serverFilters";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import ConflictResolutionDialog from "./ConflictResolutionDialog";
import DependentsWarningDialog from "./DependentsWarningDialog";
import type { DependentRecord } from "./DependentsWarningDialog";
import ViewGridToolbar from "./ViewGridToolbar";
import CsvImportDialog from "./CsvImportDialog";
import AuditLogPanel from "./AuditLogPanel";
import { DirectCellUpdateContext } from "./DirectCellUpdateContext";
import type { DirectCellUpdateFn } from "./DirectCellUpdateContext";
import { useCanCreateRow } from "../../hooks/useCanCreateRow";
import FkSelectionModal from "../pickers/FkSelectionModal";
import type { FkPrefillValue } from "../pickers/FkSelectionModal";
import { useRowOperations } from "./useRowOperations";
import {
  useBulkDeleteMutation,
  useImportCsvMutation,
} from "../../hooks/useViewDataQuery";
import type { BulkOperationResult } from "../../services/viewService";
import { CANVAS_COLOR } from "../../theme/canvasTokens";

interface ViewGridNewProps {
  view: ViewDefinition;
  data: ProcessedViewData | null;
  loading?: boolean;
  error?: string | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort?: (field: string, direction: "ASC" | "DESC") => void;
  onRowCreate?: (data: Record<string, unknown>) => Promise<void>;
  onRowUpdate?: (rowId: string, data: Record<string, unknown>) => Promise<void>;
  onRowDelete?: (rowId: string) => Promise<void>;
  onRowSelect?: (rowId: string, rowData: Record<string, unknown>) => void;
  onRefresh?: () => void;
  /** Whether to show the toolbar */
  showToolbar?: boolean;
  /** Whether to show the built-in MUI grid toolbar. */
  showGridToolbar?: boolean;
  /** Explicit grid body height in pixels. */
  height?: number;
  /** Optional ordered list of columns to display. */
  visibleColumns?: string[];
  /** Per-column display settings (status pills); see ./columnDisplay. */
  columnDisplay?: ColumnDisplayMap;
  /**
   * When set, the column-menu Filter is applied by the server: the grid reports its filters here
   * and the caller refetches. Without it the filter stays client-side over the loaded rows.
   */
  onColumnFilterChange?: (filters: ColumnFilter[]) => void;
  /** Enable record creation affordances. */
  allowCreate?: boolean;
  /** Enable inline cell editing. */
  allowEdit?: boolean;
  /** Enable row delete affordances. */
  allowDelete?: boolean;
  /** Enable bulk selection/delete affordances. */
  allowBulkActions?: boolean;
  /** Enable CSV import affordance. */
  allowImport?: boolean;
  /** Enable audit log affordance. */
  allowAuditLog?: boolean;
  writeContext?: CanvasWriteContext;
}

export default function ViewGridNew({
  view,
  data,
  loading = false,
  error = null,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSort,
  onRowCreate,
  onRowUpdate,
  onRowDelete,
  onRowSelect,
  onRefresh,
  showToolbar = true,
  showGridToolbar = true,
  height = 600,
  visibleColumns,
  columnDisplay,
  onColumnFilterChange,
  allowCreate = true,
  allowEdit = true,
  allowDelete = true,
  allowBulkActions = true,
  allowImport = true,
  allowAuditLog = true,
  writeContext,
}: ViewGridNewProps) {
  const materialGridTheme = useMaterialGridTheme();
  const apiRef = useGridApiRef();
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "danger";
  }>({ open: false, message: "", severity: "success" });

  // Conflict resolution state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState<{
    rowId: string;
    yourValues: Record<string, unknown>;
    serverValues: Record<string, unknown>;
    serverVersion: number;
  } | null>(null);

  // Dependents warning state (409 referential integrity)
  const [dependentsDialogOpen, setDependentsDialogOpen] = useState(false);
  const [dependentsData, setDependentsData] = useState<DependentRecord[]>([]);

  // FK Selection Modal state
  const [fkModalOpen, setFkModalOpen] = useState(false);

  // Audit Log panel state
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  // Row selection state for bulk operations
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowId[]>([]);

  // CSV Import dialog state
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Bulk operations hooks
  const bulkDeleteMutation = useBulkDeleteMutation();
  const importCsvMutation = useImportCsvMutation();

  // Check if we can create a row (validates FK options are available)
  const viewId = view.id?.toString();
  const effectiveOnRowCreate = allowCreate ? onRowCreate : undefined;
  const effectiveOnRowUpdate = allowEdit ? onRowUpdate : undefined;
  const effectiveOnRowDelete = allowDelete ? onRowDelete : undefined;

  // Row operations hook for inline new row creation
  const {
    newRowData,
    isCreating,
    handleAddNewRow,
    handleSaveNewRow,
    handleCancelNewRow,
    handleNewRowChange,
    rowsWithNew,
  } = useRowOperations({
    viewId,
    data: data || undefined,
    onCreateRow: effectiveOnRowCreate,
    onSuccess: (message) =>
      setSnackbar({ open: true, message, severity: "success" }),
    onError: (message) =>
      setSnackbar({ open: true, message, severity: "danger" }),
  });
  const { data: canCreateResult, isLoading: isCheckingCreate } =
    useCanCreateRow(
      allowCreate ? viewId : undefined,
      data || undefined,
      loading,
    );

  // Determine if FK modal is needed when adding a row
  const needsFkModal = (canCreateResult?.joinedTablesForModal?.length ?? 0) > 0;

  // Handle Add Record click - creates inline editable row
  const handleAddRecordClick = useCallback(() => {
    if (needsFkModal) {
      setFkModalOpen(true);
    } else {
      // Create inline editable row
      handleAddNewRow();
    }
  }, [needsFkModal, handleAddNewRow]);

  // Handle FK Modal confirm
  const handleFkModalConfirm = useCallback(
    (fkValues: Record<string, FkPrefillValue>) => {
      // Convert FkPrefillValue to simple key-value pairs
      const prefillData: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(fkValues)) {
        prefillData[key] = val.fkValue;
      }
      setFkModalOpen(false);
      if (effectiveOnRowCreate) {
        effectiveOnRowCreate(prefillData);
      }
    },
    [effectiveOnRowCreate],
  );

  // Handle FK Modal close
  const handleFkModalClose = useCallback(() => {
    setFkModalOpen(false);
  }, []);

  // Direct cell update handler for boolean fields and new row updates
  const handleDirectCellUpdate: DirectCellUpdateFn = useCallback(
    async (rowId: GridRowId, field: string, value: unknown) => {
      if (rowId === "__new_row__") {
        // Update new row state via hook
        handleNewRowChange(field, value);
      } else if (effectiveOnRowUpdate) {
        try {
          // Update existing row via mutation
          await effectiveOnRowUpdate(String(rowId), { [field]: value });
        } catch (err: any) {
          if (err?.status === 409 && err?.response) {
            const detail = err.response.detail || err.response;
            const serverRow =
              detail.current_row ||
              detail.currentRow ||
              err.response.current_row ||
              err.response.currentRow ||
              {};
            setConflictData({
              rowId: String(rowId),
              yourValues: { [field]: value },
              serverValues: serverRow,
              serverVersion: serverRow._version,
            });
            setConflictDialogOpen(true);
          }
          // Other errors are already handled by the mutation's onError
        }
      }
    },
    [effectiveOnRowUpdate, handleNewRowChange],
  );

  // Handle row selection change
  const handleRowSelectionModelChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      // GridRowSelectionModel can be an array or a Set-like object
      const ids = Array.isArray(newSelectionModel)
        ? newSelectionModel
        : Array.from(newSelectionModel);
      setSelectedRowIds(ids);
    },
    [],
  );

  // Handle bulk delete
  const handleBulkDelete = useCallback(async () => {
    if (!viewId || selectedRowIds.length === 0) return;
    const rowIdStrings = selectedRowIds.map(String);
    try {
      const result = await bulkDeleteMutation.mutateAsync({
        viewId,
        rowIds: rowIdStrings,
      });
      setSelectedRowIds([]);
      setSnackbar({
        open: true,
        message: `Deleted ${result.success_count} record(s)${result.error_count > 0 ? `, ${result.error_count} failed` : ""}`,
        severity: result.error_count > 0 ? "danger" : "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "Failed to delete selected records",
        severity: "danger",
      });
    }
  }, [viewId, selectedRowIds, bulkDeleteMutation]);

  // Handle CSV import
  const handleCsvImport = useCallback(
    async (file: File): Promise<BulkOperationResult> => {
      if (!viewId) throw new Error("No view selected");
      return importCsvMutation.mutateAsync({ viewId, file });
    },
    [viewId, importCsvMutation],
  );

  // Handle delete button click - opens confirmation dialog
  const handleDeleteClick = useCallback((rowId: string) => {
    setRowToDelete(rowId);
    setDeleteDialogOpen(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (rowToDelete && effectiveOnRowDelete) {
      setIsDeleting(true);
      try {
        await effectiveOnRowDelete(rowToDelete);
        setSnackbar({
          open: true,
          message: "Record deleted successfully",
          severity: "success",
        });
      } catch (err: any) {
        if (err?.status === 409 && err?.response?.detail?.dependents) {
          // Referential integrity violation -- show dependents warning
          setDependentsData(err.response.detail.dependents);
          setDependentsDialogOpen(true);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to delete record",
            severity: "danger",
          });
        }
      } finally {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
        setRowToDelete(null);
      }
    }
  }, [rowToDelete, effectiveOnRowDelete]);

  // Handle delete dialog close
  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  }, []);

  // Handle conflict resolution: "Keep Mine" -- retry update with the server's current _version
  const handleConflictKeepMine = useCallback(
    async (yourData: Record<string, unknown>) => {
      if (!conflictData || !effectiveOnRowUpdate) return;
      setConflictDialogOpen(false);
      try {
        await effectiveOnRowUpdate(conflictData.rowId, {
          ...yourData,
          _version: conflictData.serverVersion,
        });
        setSnackbar({
          open: true,
          message: "Record updated successfully (conflict resolved)",
          severity: "success",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to update record after conflict resolution",
          severity: "danger",
        });
      }
      setConflictData(null);
    },
    [conflictData, effectiveOnRowUpdate],
  );

  // Handle conflict resolution: "Use Theirs" -- discard local changes, refresh data
  const handleConflictUseTheirs = useCallback(() => {
    setConflictDialogOpen(false);
    setConflictData(null);
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  // Handle conflict dialog close (Cancel)
  const handleConflictClose = useCallback(() => {
    setConflictDialogOpen(false);
    setConflictData(null);
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  // Wrapper for save that reads current row data from grid API
  // This ensures we get any uncommitted cell edits before saving
  const handleSaveNewRowWithApiData = useCallback(async () => {
    // First, stop any active cell editing to commit the values
    // We need to check all editable columns for the new row
    if (data?.columns) {
      for (const col of data.columns) {
        try {
          const cellMode = apiRef.current.getCellMode("__new_row__", col.field);
          if (cellMode === "edit") {
            // Stop editing this cell (commits the value)
            apiRef.current.stopCellEditMode({
              id: "__new_row__",
              field: col.field,
            });
          }
        } catch {
          // Cell might not exist or already be in view mode
        }
      }
    }

    // Small delay to ensure values are committed
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Now get the row with committed values
    const rowData = apiRef.current.getRow("__new_row__");
    if (rowData) {
      await handleSaveNewRow(rowData as Record<string, unknown>);
    }
  }, [apiRef, handleSaveNewRow, data?.columns]);

  // Build columns using the hook
  const { columns } = useViewGridColumns({
    data: data || undefined,
    viewId,
    columnOrder,
    setColumnOrder,
    onDeleteRow: effectiveOnRowDelete ? handleDeleteClick : undefined,
    isDeleting,
    // New row operations
    onSaveNewRow: newRowData ? handleSaveNewRowWithApiData : undefined,
    onCancelNewRow: newRowData ? handleCancelNewRow : undefined,
    isCreating,
    // File column support
    focusTableId: view.focus_table_id,
    onRefresh,
    writeContext,
    allowEdit,
  });
  const displayedColumns = useMemo(() => {
    const shown = orderedVisibleColumns(columns, visibleColumns);
    const displayed = applyColumnDisplay(shown, columnDisplay);
    // Server-side filtering offers only the operators the server can apply.
    if (!onColumnFilterChange) return displayed;
    const isFilterable = serverFilterableColumns(data?.columns || []);
    return displayed.map((column) => ({
      ...column,
      filterable: column.filterable !== false && isFilterable(column.field),
      filterOperators: serverFilterOperators(column.type),
    }));
  }, [
    columns,
    visibleColumns,
    columnDisplay,
    onColumnFilterChange,
    data?.columns,
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);
      onColumnFilterChange?.(toServerFilters(model));
    },
    [onColumnFilterChange],
  );

  // Determine if user can add records
  const canAddRecord = !!(
    effectiveOnRowCreate && canCreateResult?.canCreate !== false
  );
  const addRecordDisabledReason = canCreateResult?.reason;

  // Handle single-click to start editing
  const handleCellClick = useCallback((params: GridCellParams) => {
    // Don't allow editing the actions column
    if (params.field === "_actions") {
      return;
    }
    // Only enter edit mode if the cell is editable
    if (params.isEditable) {
      setCellModesModel((prevModel) => ({
        // Reset all other cells to view mode
        ...Object.keys(prevModel).reduce((acc, id) => {
          acc[id] = Object.keys(prevModel[id] || {}).reduce(
            (fieldAcc, field) => {
              fieldAcc[field] = { mode: GridCellModes.View };
              return fieldAcc;
            },
            {} as Record<string, { mode: GridCellModes }>,
          );
          return acc;
        }, {} as GridCellModesModel),
        // Set clicked cell to edit mode
        [params.id]: {
          ...prevModel[params.id],
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    }
  }, []);

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      const rowId = getViewRowId(params.row);
      if (!onRowSelect || !rowId || rowId === "__new_row__") return;
      onRowSelect(rowId, params.row as Record<string, unknown>);
    },
    [onRowSelect],
  );

  // Handle cell modes model change
  const handleCellModesModelChange = useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    [],
  );

  // Handle pagination model change
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.page !== page) {
        onPageChange(model.page);
      }
      if (model.pageSize !== pageSize) {
        onPageSizeChange(model.pageSize);
      }
    },
    [page, pageSize, onPageChange, onPageSizeChange],
  );

  // Handle sort model change
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (onSort && model.length > 0) {
        const { field, sort } = model[0];
        onSort(field, sort === "asc" ? "ASC" : "DESC");
      }
    },
    [onSort],
  );

  // Handle row update (cell edit)
  const processRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow: GridRowModel,
    ): Promise<GridRowModel> => {
      // Find the row ID
      const rowId = getViewRowId(newRow);
      if (!rowId) return oldRow;

      // For new rows, just update local state - don't send to API yet
      if (rowId === "__new_row__") {
        // Find changed fields and update via hook
        for (const key of Object.keys(newRow)) {
          if (
            key !== "_rowId" &&
            key !== "__row_id" &&
            newRow[key] !== oldRow[key]
          ) {
            handleNewRowChange(key, newRow[key]);
          }
        }
        return newRow; // Return updated row for grid display
      }

      // For existing rows, send changes to API
      if (!effectiveOnRowUpdate) return oldRow;

      // Find changed fields
      const changes: Record<string, unknown> = {};
      for (const key of Object.keys(newRow)) {
        if (
          key !== "_rowId" &&
          key !== "__row_id" &&
          newRow[key] !== oldRow[key]
        ) {
          changes[key] = newRow[key];
        }
      }

      if (Object.keys(changes).length === 0) {
        return oldRow; // No changes
      }

      try {
        await effectiveOnRowUpdate(rowId, changes);
        setSnackbar({
          open: true,
          message: "Record updated successfully",
          severity: "success",
        });
        return newRow;
      } catch (err: any) {
        if (err?.status === 409 && err?.response) {
          // Optimistic locking conflict -- show resolution dialog
          const detail2 = err.response.detail || err.response;
          const serverRow =
            detail2.current_row ||
            detail2.currentRow ||
            err.response.current_row ||
            err.response.currentRow ||
            {};
          setConflictData({
            rowId,
            yourValues: changes,
            serverValues: serverRow,
            serverVersion: serverRow._version,
          });
          setConflictDialogOpen(true);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to update record",
            severity: "danger",
          });
        }
        return oldRow; // Revert on error
      }
    },
    [effectiveOnRowUpdate, handleNewRowChange],
  );

  // Handle row update error
  const handleProcessRowUpdateError = useCallback((error: Error) => {
    // Don't show error snackbar for 409 conflicts -- handled by conflict dialog
    if ((error as any)?.status === 409) return;
    console.error("Row update error:", error);
    setSnackbar({
      open: true,
      message: "Failed to update record",
      severity: "danger",
    });
  }, []);

  // Close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Rows to display - includes new row if being created
  const rows = rowsWithNew;

  if (error) {
    return (
      <Alert color="danger" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <DirectCellUpdateContext.Provider value={handleDirectCellUpdate}>
      <Box>
        {/* Toolbar */}
        {showToolbar && (
          <Tooltip title={addRecordDisabledReason || ""} placement="top">
            <span>
              <ViewGridToolbar
                viewId={viewId || ""}
                onAddRecord={canAddRecord ? handleAddRecordClick : undefined}
                canAddRecord={canAddRecord}
                isLoading={isCheckingCreate}
                onAuditLog={
                  allowAuditLog ? () => setAuditLogOpen(true) : undefined
                }
                onImportCsv={
                  allowImport ? () => setCsvImportOpen(true) : undefined
                }
                selectedCount={selectedRowIds.length}
                onBulkDelete={
                  allowBulkActions && effectiveOnRowDelete
                    ? handleBulkDelete
                    : undefined
                }
              />
            </span>
          </Tooltip>
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            {data?.totalCount || 0}{" "}
            {(data?.totalCount || 0) === 1 ? "row" : "rows"}
          </Typography>
        </Box>

        {/* DataGrid - wrapped in Material UI ThemeProvider for compatibility */}
        <ThemeProvider theme={materialGridTheme}>
          <Box
            sx={{
              width: "100%",
              height,
              "& .MuiDataGrid-root": {
                color: materialGridThemeTokens.textPrimary,
                backgroundColor: materialGridThemeTokens.backgroundSurface,
              },
              "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-footerContainer": {
                backgroundColor: materialGridThemeTokens.backgroundSurface,
              },
              // Styling for REFERENCE mode cells (link fields)
              "& .reference-edit-cell": {
                cursor: "pointer",
                backgroundColor: CANVAS_COLOR.referenceCellBg,
                "&:hover": {
                  backgroundColor: CANVAS_COLOR.referenceCellHoverBg,
                },
                "& .MuiDataGrid-cellContent": {
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  color: "primary.main",
                },
              },
              // Styling for DIRECT mode cells (normal editable)
              "& .direct-edit-cell": {
                cursor: "text",
              },
              // New row highlighting
              "& .MuiDataGrid-row[data-id='__new_row__']": {
                backgroundColor: CANVAS_COLOR.newRowBg,
                "&:hover": {
                  backgroundColor: CANVAS_COLOR.newRowHoverBg,
                },
              },
            }}
          >
            <DataGrid
              apiRef={apiRef}
              rows={rows}
              columns={displayedColumns}
              getRowId={(row: Record<string, unknown>) =>
                // This key renders anonymous rows only; mutation paths use getViewRowId.
                getViewRowId(row) ?? `display-only-row-${rows.indexOf(row)}`
              }
              loading={loading}
              onRowClick={handleRowClick}
              // Editing
              editMode="cell"
              cellModesModel={cellModesModel}
              onCellModesModelChange={handleCellModesModelChange}
              isCellEditable={(params: GridCellParams) => {
                if (params.field === "_actions" || !getViewRowId(params.row))
                  return false;
                return allowEdit && (params.colDef.editable ?? false);
              }}
              onCellClick={handleCellClick}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              // Pagination
              pagination
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={handlePaginationModelChange}
              rowCount={data?.totalCount || 0}
              pageSizeOptions={[10, 25, 50, 100]}
              // Row selection for bulk operations
              checkboxSelection={allowBulkActions}
              isRowSelectable={(params) =>
                !!getViewRowId(params.row) &&
                getViewRowId(params.row) !== "__new_row__"
              }
              disableRowSelectionOnClick={!allowBulkActions}
              rowSelectionModel={selectedRowIds}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              // Sorting
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              // Filtering: by the server when the caller handles it
              filterMode={onColumnFilterChange ? "server" : "client"}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              // Toolbar
              slots={showGridToolbar ? { toolbar: GridToolbar } : undefined}
              slotProps={{
                toolbar: {
                  showQuickFilter: false,
                },
                // The server ANDs the filters; OR between columns is not offered.
                filterPanel: {
                  logicOperators: [GridLogicOperator.And],
                },
              }}
              // Styling
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            />
          </Box>
        </ThemeProvider>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          color={snackbar.severity}
        >
          {snackbar.message}
        </Snackbar>

        {/* Delete Confirmation Dialog */}
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />

        {/* Dependents Warning Dialog (referential integrity) */}
        <DependentsWarningDialog
          open={dependentsDialogOpen}
          onClose={() => {
            setDependentsDialogOpen(false);
            setDependentsData([]);
          }}
          dependents={dependentsData}
        />

        {/* Conflict Resolution Dialog */}
        {conflictData && (
          <ConflictResolutionDialog
            open={conflictDialogOpen}
            onClose={handleConflictClose}
            onKeepMine={handleConflictKeepMine}
            onUseTheirs={handleConflictUseTheirs}
            yourValues={conflictData.yourValues}
            serverValues={conflictData.serverValues}
            columns={(data?.columns || []).map((col) => ({
              field: col.field,
              headerName: col.headerName,
            }))}
          />
        )}

        {/* FK Selection Modal */}
        {viewId && needsFkModal && (
          <FkSelectionModal
            open={fkModalOpen}
            onClose={handleFkModalClose}
            onConfirm={handleFkModalConfirm}
            viewId={viewId}
            joinedTables={canCreateResult?.joinedTablesForModal || []}
          />
        )}

        {/* CSV Import Dialog */}
        <CsvImportDialog
          open={csvImportOpen}
          onClose={() => setCsvImportOpen(false)}
          onImport={handleCsvImport}
        />

        {/* Audit Log Panel */}
        {viewId && (
          <AuditLogPanel
            open={auditLogOpen}
            onClose={() => setAuditLogOpen(false)}
            viewId={viewId}
          />
        )}
      </Box>
    </DirectCellUpdateContext.Provider>
  );
}
